import * as React from 'react';
import { useState, useCallback, useMemo, useRef } from 'react';

/** Material UI Packages. */
import { Box } from '@mui/material';
import { DataGridPremium, GridRowModes, GridToolbar, useGridApiRef } from '@mui/x-data-grid-premium';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons */
import AddIcon from '@mui/icons-material/Add';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { createAndAppendRoute, receivedRoutes, updatePaginationModel } from 'shared/store/slices/routeSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { dataGridOverrideStyle, StyledBadge } from 'shared/config/Styles';
import CustomSnackbar from 'shared/ui/CustomSnackbar.js';
import ThemeButton, { DialogButton } from 'shared/ui/Button';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import { readSocketData } from 'shared/lib/fetchSocket';

import NewUpdatesDialog from './NewUpdatesDialog';
import { routeDef } from '../../lib/RouteDef';
import RouteDataGridHandlers from './Handler/RouteDataGridHandlers.js';
import RouteColumnHandlers from './Handler/RouteColumnHandlers.js';

export default function TransportRoute(props) {
    /** Get props from DataEntry.js */
    const { loading, tabValue } = props;

    /** Access datagrid API object through apiRef. */
    const apiRef = useGridApiRef();
    /** State to save the previous row value before update. */
    const restoreRouteRef = useRef({});

    /** Load dispatch to update routes. */
    const dispatch = useDispatch();

    /** State for setting datagrid's row information and datagrid's pagination settings. */
    const routeData = useSelector((state) => state.routes);
    /** State for saving the new updated routes. */
    const newRouteList = useSelector((state) => state.newRouteList);

    /** State for changing the mode of row. */
    const [rowModesModel, setRowModesModel] = useState({});
    /** State for open and close new updates dialog. */
    const [newOpen, setNewOpen] = useState(false);

    /** Check if any row is editing. */
    const isAnyRowEditing = useMemo(
        () => Object.values(rowModesModel).some((modeObj) => modeObj.mode === GridRowModes.Edit),
        [rowModesModel]
    );

    /** Function to save the previous row value in restoreRoute. */
    const savePrevRoute = useCallback(
        (id) => {
            const prevRoute = routeData.il_route.find((route) => route.id === id);
            restoreRouteRef.current[id] = prevRoute;
        },
        [routeData.il_route]
    );

    /** Handler to create new route. Generate empty route with random id. */
    const handleNewClick = useCallback(
        (rowModesModel) => {
            if (isAnyRowEditing) {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Please complete editing the route before creating a new route.',
                        severity: 'warning',
                    })
                );
                return;
            }

            const newId = randomId();
            dispatch(createAndAppendRoute({ id: newId }));

            setRowModesModel((prev) => ({
                ...Object.fromEntries(
                    Object.entries(prev).map(([rowId, mode]) => [
                        rowId,
                        mode.mode === GridRowModes.Edit ? { mode: GridRowModes.Edit } : mode,
                    ])
                ),
                [newId]: { mode: GridRowModes.Edit },
            }));

            apiRef.current.setCellFocus(newId, 'pol_name');
        },
        [isAnyRowEditing, dispatch, apiRef]
    );

    const handlePaginationModelChange = useCallback(
        (newPaginationModel) => {
            dispatch(updatePaginationModel(newPaginationModel));
            readSocketData(
                dispatch,
                tabValue,
                'row',
                'il_route',
                null,
                JSON.stringify(newPaginationModel),
                (receivedData) => {
                    dispatch(receivedRoutes(receivedData));
                }
            );
        },
        [routeData.paginationModel, tabValue]
    );

    /** Load data grid handlers. */
    const { processRowUpdate, onRowEditStart, handleRowEditStop, handleCellKeyDown, groupingModel } =
        RouteDataGridHandlers({
            tabValue,
            rowModesModel,
            setRowModesModel,
            savePrevRoute,
            restoreRouteRef,
        });

    /** Load handler that used in column definitions.*/
    const { columnDefFeature } = RouteColumnHandlers({
        rowModesModel,
        setRowModesModel,
        tabValue,
        apiRef,
        restoreRouteRef,
        savePrevRoute,
    });

    if (loading) {
        return <CustomSkeleton2 />;
    }

    return (
        <Box sx={{ my: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'right', gap: '16px' }}>
                <StyledBadge badgeContent={newRouteList.length > 0 ? 'N' : 0} color="error" size="large">
                    <DialogButton
                        text={'NEW UPDATES'}
                        onClick={() => setNewOpen(true)}
                        sx={{ width: '123px', border: 'solid 1px' }}
                    />
                </StyledBadge>
                <ThemeButton
                    text={'NEW'}
                    icon={<AddIcon />}
                    width="62px"
                    onClick={() => handleNewClick(rowModesModel)}
                />
            </Box>
            <Box>
                <DataGridPremium
                    rows={routeData.il_route || []}
                    columns={routeDef(tabValue, rowModesModel, columnDefFeature)}
                    editMode="row"
                    rowModesModel={rowModesModel}
                    apiRef={apiRef}
                    columnGroupingModel={groupingModel(tabValue)}
                    columnHeaderHeight={30}
                    rowHeight={40}
                    loading={loading}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={(error) => {
                        console.log('error: ', error.message);
                    }}
                    onRowEditStart={onRowEditStart}
                    onRowEditStop={handleRowEditStop}
                    onRowModesModelChange={(newRowModesModel) => setRowModesModel(newRowModesModel)}
                    onCellKeyDown={handleCellKeyDown}
                    rowCount={routeData.rowCount}
                    paginationModel={routeData.paginationModel}
                    pagination={true}
                    pageSizeOptions={[25, 50, 100]}
                    paginationMode="server"
                    onPaginationModelChange={handlePaginationModelChange}
                    disableAggregation
                    disableRowSelectionOnClick
                    showCellVerticalBorder
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                id: false,
                            },
                        },
                    }}
                    sx={{
                        fontSize: 13,
                        border: 'none',
                        justifyContent: 'space-between',
                        overflow: 'visible',
                        height: '80vh',
                        ...dataGridOverrideStyle,
                    }}
                />
            </Box>
            <NewUpdatesDialog open={newOpen} setOpen={setNewOpen} tabValue={tabValue} />
            <CustomSnackbar />
        </Box>
    );
}
